import React, { useState, useEffect } from 'react'
import { Building } from 'lucide-react'
import { supabase } from '../../shared/api/supabase'
import { useAuth } from '../../shared/contexts/AuthContext'

interface Tenant {
  id: string
  name: string
  domain?: string
}

interface TenantContextIndicatorProps {
  className?: string
}

export default function TenantContextIndicator({ className = '' }: TenantContextIndicatorProps) {
  const { user } = useAuth()
  const [tenant, setTenant] = useState<Tenant | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!user?.id) {
      setLoading(false)
      return
    }

    const fetchTenant = async () => {
      try {
        // First get user's tenant_id from database
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('tenant_id')
          .eq('id', user.id)
          .single()

        if (userError || !userData?.tenant_id) {
          setLoading(false)
          return
        }

        // Then fetch tenant info
        const { data, error } = await supabase
          .from('tenants')
          .select('id, name, domain')
          .eq('id', userData.tenant_id)
          .single()

        if (error) {
          console.error('Error fetching tenant:', error)
          setTenant(null)
        } else {
          setTenant(data)
        }
      } catch (error) {
        console.error('Error fetching tenant:', error)
        setTenant(null)
      } finally {
        setLoading(false)
      }
    }

    fetchTenant()
  }, [user?.id])

  // Don't show if no tenant
  if (loading || !tenant) {
    return null
  }

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 bg-white/10 dark:bg-gray-800/50 rounded-lg border border-white/20 dark:border-gray-700 ${className}`}>
      <Building className="w-4 h-4 text-white dark:text-gray-300" />
      <span className="text-sm font-medium text-white dark:text-gray-300 truncate max-w-[120px]">
        {tenant.name}
      </span>
    </div>
  )
}

